import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Armchair, ArrowRight, ArrowsClockwise, ChartBar, CursorClick, Flag, UsersThree } from '@phosphor-icons/react';
import { Slide } from '../Slide';
import { InView } from '../InView';
import { SlideHeadline } from '../SlideHeadline';
import { SiblingFigure } from '../../characters/SiblingFigure';
import { products } from '../../../data/products';
import { EmbeddedSnakWindow } from '../EmbeddedSnakWindow';
import { campaignReportPdfUrl, measures, signalLoop } from '../../../data/deck';
import type { MeasureId } from '../../../data/deck';
export function SignalsSlide() {
  const [reportOpen, setReportOpen] = useState(false);
  return <Slide id="signals" data-id="element-333" data-mp="32450u278hfz6">
      <AnimatePresence data-id="element-334" data-mp="323dg5mf6d2no">
        {reportOpen && <EmbeddedSnakWindow key="report" url={campaignReportPdfUrl} title="Filtek Clinical Case Series · Campaign report" shape="document" onClose={() => setReportOpen(false)} data-id="element-335" data-mp="3pu91q1t1f1mh" />}
      </AnimatePresence>
      <div className="grid items-center gap-14 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-16" data-id="element-336" data-mp="3cyhrylou935d">
        <div data-id="element-337" data-mp="3rmvv6xq62tmg">
          <InView data-id="element-338" data-mp="3rndvq6jv3u06">
            <SlideHeadline lead="More engagement." follow="More insight." data-id="element-339" data-mp="3rnxf7rp5uqgb" />
          </InView>
          <InView delay={0.1} data-id="element-340" data-mp="3mvni9rpmoq4j">
            <p className="mt-8 max-w-[38ch] font-heading text-[19px] leading-[1.35] text-[rgba(1,51,43,0.62)]" data-id="element-341" data-mp="3mucehrw6nidf">
              The concept doesn't just communicate. It helps us understand how customers
              prefer to engage.
            </p>
          </InView>
          <InView delay={0.2} className="mt-12" data-id="element-342" data-mp="efav4sb3kcor">
            <div className="flex flex-wrap items-center gap-2" data-id="element-343" data-mp="9hj37yagcqkk">
              {signalLoop.map((step, i) => <React.Fragment key={step} data-id="element-344" data-mp="2g7p8rf6lyuzd" data-mpi={i}>
                  <span className="rounded-full bg-white px-4 py-2 font-body text-[12px] text-background ring-1 ring-[rgba(1,51,43,0.12)]" data-id="element-345" data-mp="2g8ik6tgii8nu">
                    {step}
                  </span>
                  {i < signalLoop.length - 1 && <ArrowRight size={14} className="text-[rgba(1,51,43,0.4)]" aria-hidden="true" data-id="element-346" data-mp="34ymxexbsqd0d" />}
                </React.Fragment>)}
            </div>
            <p className="mt-8 flex items-center gap-3 font-heading text-[24px] leading-tight text-background" data-id="element-347" data-mp="3ey40d6znycsn">
              <span aria-hidden="true" className="h-[2px] w-8 rounded-full bg-primary" data-id="element-348" data-mp="3ju2tf7vxsize" />
              Learn what works. Do more of it.
            </p>
            <button type="button" onClick={() => setReportOpen(true)} className="mt-10 inline-flex items-center gap-2.5 rounded-full bg-background px-6 py-3.5 font-body text-[14px] font-medium text-white shadow-[0_18px_40px_-20px_rgba(1,51,43,0.65)] transition-colors hover:bg-[#02463b] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-349" data-mp="3sp8qnih55sqb">
              <ChartBar size={16} weight="bold" className="text-primary" aria-hidden="true" data-id="element-350" data-mp="1vw1rn1qabd11" />
              Campaign Data
            </button>
          </InView>
        </div>

        <InView delay={0.15} y={26} data-id="element-351" data-mp="1m0qdakisg7tt">
          <figure className="rounded-[30px] bg-background p-6 text-white shadow-[0_50px_90px_-50px_rgba(1,51,43,0.8)] sm:p-8" data-id="element-352" data-mp="1m1ruz7ub7ski">
            <div className="flex items-center justify-between" data-id="element-353" data-mp="2ep5loo7ezrw6">
              <span className="font-body text-[10px] uppercase tracking-[0.22em] text-primary" data-id="element-354" data-mp="2eqgretkf79px">
                Campaign intelligence
              </span>
              <span className="font-body text-[10px] uppercase tracking-[0.16em] text-white/40" data-id="element-355" data-mp="24xrjg8o2wdzc">
                Illustrative
              </span>
            </div>

            <div className="mt-7 grid grid-cols-4 gap-3" data-id="element-356" data-mp="2xeu45hu2xfun">
              {products.map(product => <div key={product.id} className="flex flex-col items-center text-center" data-id="element-357" data-mp="2snvjy75yzxm4">
                  <SiblingFigure role={product.answer} className="h-16 w-16 sm:h-20 sm:w-20" data-id="element-358" data-mp="2d6nj7my2p7cl" />
                  <span className="mt-2.5 font-heading text-[13px] leading-tight text-white" data-id="element-359" data-mp="2i5gud8pw0m9l">
                    {product.shortName}
                  </span>
                  <span className="mt-0.5 font-body text-[9.5px] uppercase tracking-[0.12em] text-white/45" data-id="element-360" data-mp="239rabg49ij7i">
                    {product.answerLabel.replace('The ', '')}
                  </span>
                </div>)}
            </div>

            <ol className="mt-8 border-t border-white/10" data-id="element-361" data-mp="30whu3yyu0lu7">
              {measures.map((measure, i) => <li key={measure.id} className="flex items-center gap-4 border-b border-white/10 py-3.5 last:border-b-0" data-id="element-362" data-mp="2m7lm21wrz8tl" data-mpi={i}>
                  <span className="w-5 font-body text-[10px] tabular-nums text-white/35" data-id="element-363" data-mp="3evpx72y48e3o">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/[0.07] text-primary" data-id="element-364" data-mp="3jnr6cfzzloph">
                    <MeasureGlyph id={measure.id} data-id="element-365" data-mp="138dzbsm261xi" />
                  </span>
                  <span className="font-body text-[13.5px] leading-snug text-white/85" data-id="element-366" data-mp="1369zpmv6zp31">
                    {measure.question}
                  </span>
                </li>)}
            </ol>
            <figcaption className="mt-4 font-body text-[11px] leading-relaxed text-white/45" data-id="element-367" data-mp="1d0a8byponcgi">
              Signals inform the next campaign and improve activation.
            </figcaption>
          </figure>
        </InView>
      </div>
    </Slide>;
}
function MeasureGlyph({
  id
}: {
  id: MeasureId;
}) {
  const size = 17;
  if (id === 'leanBack') return <Armchair size={size} aria-hidden="true" data-id="element-368" data-mp="u7g2pvdy8woy" />;
  if (id === 'interest') return <UsersThree size={size} aria-hidden="true" data-id="element-369" data-mp="29o6kmre37llx" />;
  if (id === 'progress') return <Flag size={size} aria-hidden="true" data-id="element-370" data-mp="29qawbvi89owr" />;
  if (id === 'ctas') return <CursorClick size={size} aria-hidden="true" data-id="element-371" data-mp="25mbjbmw5p885" />;
  return <ArrowsClockwise size={size} aria-hidden="true" data-id="element-372" data-mp="12c7eenle3tu0" />;
}